import Vue from "vue";

// Caches the raw Zendesk API payload in localStorage so a high-traffic
// knowledge base doesn't re-fetch/re-paginate the entire article set from
// Zendesk on every single page load. Cache the pre-sort payload only (not
// the store's sorted/grouped copy) so sortZendeskData stays the single
// source of truth for that transform.
const CACHE_KEY = "rmr_zendesk_cache_v1";
const CACHE_TTL_MS = 15 * 60 * 1000; // 15 minutes

function readCache() {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { savedAt, data } = JSON.parse(raw);
    if (!savedAt || Date.now() - savedAt > CACHE_TTL_MS) return null;
    return data;
  } catch (e) {
    return null;
  }
}

function writeCache(data) {
  try {
    window.localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ savedAt: Date.now(), data })
    );
  } catch (e) {
    // Ignore quota/private-browsing errors — caching is a best-effort
    // optimization, not something the app depends on.
  }
}

const state = {
  zendeskData: [],
  zendeskError: false,
  categories: [
    {
      id: 360002944813,
      name: "Participants/Employees",
      routeName: "participants"
    },
    { id: 360002944833, name: "Employers", routeName: "employers" }
  ]
};

const getters = {
  allZendeskData(state) {
    return state.zendeskData;
  },
  zendeskError(state) {
    return state.zendeskError;
  },
  categoryById: state => id => {
    if (state.zendeskData && state.zendeskData.categories) {
      return state.zendeskData.categories.find(category => category.id === id);
    } else {
      return null;
    }
  },
  sectionsByCategoryId: state => id => {
    if (state.zendeskData && state.zendeskData.sections) {
      return state.zendeskData.sections.filter(
        section => section.category_id === id
      );
    } else {
      return null;
    }
  },
  sectionById: state => id => {
    if (state.zendeskData && state.zendeskData.sections) {
      return state.zendeskData.sections.find(section => section.id === id);
    } else {
      return null;
    }
  },

  articleById: state => id => {
    if (state.zendeskData === undefined) {
      return null;
    } else if (state.zendeskData.length == 0) {
      return null;
    } else if (state.zendeskData.articles === undefined) {
      return null;
    } else if (state.zendeskData.articles.length === 0) {
      return null;
    } else {
      var article = state.zendeskData.articles.find(
        article => article.id == id
      );
      return article;
    }
  },
  promotedArticles(state) {
    if (state.zendeskData && state.zendeskData.articles) {
      return state.zendeskData.articles.filter(article => article.promoted);
    }
    return [];
  },
  routeNameBySectionId: state => id => {
    if (state.zendeskData === undefined) {
      return null;
    } else if (state.zendeskData.length == 0) {
      return null;
    } else if (state.zendeskData.sections === undefined) {
      return null;
    } else if (state.zendeskData.sections.length === 0) {
      return null;
    } else {
      var categoryId = state.zendeskData.sections.find(
        section => section.id === id
      ).category_id;
      var routeName = state.categories.find(
        category => category.id === categoryId
      ).routeName;
      return routeName;
    }
  }
};

const actions = {
  async fetchZendeskData({ commit, state }) {
    if (state.zendeskData === undefined || state.zendeskData.length === 0) {
      // console.log("Fetching Zendesk Data...");
      commit("setZendeskError", false);

      const cached = readCache();
      if (cached) {
        commit("setZendeskData", cached);
        commit("sortZendeskData");
        return;
      }

      let url =
        "https://rmr.zendesk.com/api/v2/help_center/en-us/articles.json?per_page=100&include=categories,sections&sort_by=title&sort_order=asc";
      var allData = null;

      try {
        while (url !== null) {
          await fetch(url)
            .then(response => {
              if (!response.ok) {
                throw new Error("Zendesk request failed");
              }
              return response.json();
            })
            .then(data => {
              if (!allData) {
                allData = data;
              } else {
                allData.articles = allData.articles.concat(data.articles);

                const existingSectionIds = new Set(
                  allData.sections.map(s => s.id)
                );
                data.sections.forEach(s => {
                  if (!existingSectionIds.has(s.id)) allData.sections.push(s);
                });

                const existingCategoryIds = new Set(
                  allData.categories.map(c => c.id)
                );
                data.categories.forEach(c => {
                  if (!existingCategoryIds.has(c.id))
                    allData.categories.push(c);
                });
              }
              url = data.next_page;
            });
        }

        commit("setZendeskData", allData);
        commit("sortZendeskData");
        writeCache(allData);
      } catch (e) {
        commit("setZendeskError", true);
      }
    }
  }
};

const mutations = {
  setZendeskData: (state, zendeskData) => {
    state.zendeskData = zendeskData;
  },
  setZendeskError: (state, value) => {
    state.zendeskError = value;
  },
  sortZendeskData: state => {
    state.zendeskData.sections.forEach(section => {
      Vue.set(section, "articles", []);
    });

    state.zendeskData.articles.forEach(article => {
      let sectionIndex = state.zendeskData.sections.findIndex(
        section => section.id === article.section_id
      );

      if (sectionIndex >= 0) {
        state.zendeskData.sections[sectionIndex].articles.push(article);
      }
    });

    state.zendeskData.sections.forEach(section => {
      const subsectionMap = new Map();
      const ungrouped = [];

      section.articles.forEach(article => {
        const label = (article.label_names || []).find(l =>
          l.startsWith("subsection:")
        );
        if (label) {
          const name = label.replace("subsection:", "");
          if (!subsectionMap.has(name)) subsectionMap.set(name, []);
          subsectionMap.get(name).push(article);
        } else {
          ungrouped.push(article);
        }
      });

      if (subsectionMap.size > 0) {
        Vue.set(
          section,
          "subsections",
          Array.from(subsectionMap.entries()).map(([name, articles]) => ({
            name,
            articles
          }))
        );
        Vue.set(section, "ungroupedArticles", ungrouped);
      }
    });
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
